import {
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Mail,
  Phone,
  PhoneCall,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import API from '../../lib/api';

const STATUS_FILTERS = [
  { value: '',          label: 'All',       icon: PhoneCall,    accent: '#6C63FF' },
  { value: 'pending',   label: 'Pending',   icon: Clock,        accent: '#F97316' },
  { value: 'confirmed', label: 'Confirmed', icon: CheckCircle,  accent: '#3B82F6' },
  { value: 'completed', label: 'Completed', icon: CheckCircle,  accent: '#10B981' },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle,      accent: '#E57373' },
];

const STATUS_BADGE = {
  pending:   'bg-orange-100 text-orange-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

export default function CallRequestsPage() {
  const { user } = useAuth();
  const [data, setData]       = useState({ calls: [], total: 0, counts: {}, page: 1, pages: 1 });
  const [status, setStatus]   = useState('pending');
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (status) params.status = status;
      const { data: res } = await API.get('/admin/scheduled-calls', { params });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load call requests');
    }
    setLoading(false);
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const updateCall = async (id, newStatus, notes = '') => {
    setActingId(id);
    try {
      await API.put(`/admin/scheduled-calls/${id}`, { status: newStatus, notes });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Update failed');
    }
    setActingId(null);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Page through every matching call (server caps limit at 100).
      const baseParams = {};
      if (status) baseParams.status = status;

      const all = [];
      let p = 1;
      while (true) {
        const { data: res } = await API.get('/admin/scheduled-calls', {
          params: { ...baseParams, page: p, limit: 100 },
        });
        all.push(...(res.calls || []));
        if (p >= (res.pages || 1) || (res.calls || []).length === 0) break;
        p += 1;
      }

      const headers = [
        'Call ID', 'Customer Name', 'Customer Email', 'Customer Phone',
        'Slot', 'Slot At', 'Service', 'Stage', 'Order ID',
        'Assigned CA', 'CA Email', 'Status', 'Response Notes', 'Requested',
      ];

      // RFC 4180: quote if value contains a comma, quote, or newline.
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = all.map((c) => {
        const isRescheduled =
          c.status === 'cancelled' && /^Rescheduled/i.test(c.response_notes || '');
        return [
          `#${c.id}`,
          c.user?.name,
          c.user?.email,
          c.user?.phone,
          c.slot_label,
          c.slot_at ? new Date(c.slot_at).toLocaleString('en-IN') : '',
          c.service_id,
          c.stage,
          c.order_id ? `#${c.order_id}` : '',
          c.ca?.name || 'Unassigned',
          c.ca?.email,
          isRescheduled ? 'Rescheduled' : c.status,
          c.response_notes,
          c.created_at ? new Date(c.created_at).toLocaleString('en-IN') : '',
        ].map(esc).join(',');
      });

      const csv = [headers.map(esc).join(','), ...rows].join('\r\n');
      // UTF-8 BOM so Excel renders Indian names and ₹ correctly.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = status ? `-${status}` : '';
      a.href = url;
      a.download = `call-requests${suffix}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Failed to export call requests');
    }
    setExporting(false);
  };

  const accept   = (call) => updateCall(call.id, 'confirmed');
  const reject   = (call) => {
    const reason = window.prompt('Optional: reason for rejecting?', '') || '';
    updateCall(call.id, 'cancelled', reason);
  };
  const complete = (call) => updateCall(call.id, 'completed');

  const isCa = user?.role === 'ca';

  return (
    <div className="space-y-5">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F4C3A] via-[#1A6F4D] to-[#10B981] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-white/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#34D399]/25 rounded-full blur-3xl" />
        <div className="relative flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <PhoneCall className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Call Requests
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                {isCa ? 'Calls assigned to you' : 'Customer call requests across all CAs'}
              </p>
            </div>
          </div>

          {/* Status filters with live counts */}
          <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
            {STATUS_FILTERS.map((f) => {
              const Icon = f.icon;
              const active = status === f.value;
              // "All" sums every bucket; the rest read their own count.
              const count = f.value === ''
                ? Object.values(data.counts || {}).reduce((a, b) => a + b, 0)
                : (data.counts?.[f.value] ?? 0);
              return (
                <button
                  key={f.value}
                  onClick={() => { setStatus(f.value); setPage(1); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-body font-medium transition-all ${
                    active
                      ? 'bg-white text-[#0F4C3A] border-white shadow-sm'
                      : 'bg-white/5 border-white/20 text-white/85 hover:bg-white/10'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color: active ? f.accent : undefined }} />
                  {f.label}
                  <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-heading font-bold ${
                    active ? 'bg-[#0F4C3A]/10 text-[#0F4C3A]' : 'bg-white/15 text-white'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Toolbar — keeps the table card uncluttered */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-body text-[#6B7280]">
          {data.total} {data.total === 1 ? 'call' : 'calls'}{status ? ` · ${status}` : ''}
        </p>
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          title="Download all matching call requests as CSV"
          className="flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* Body */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
          </div>
        ) : data.calls.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <PhoneCall className="h-8 w-8 text-[#9CA3AF] mb-2" />
            <p className="text-sm text-[#6B7280] font-body">No call requests in this view</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {['Customer', 'When', 'Service', 'Order', 'Assigned CA', 'Status', 'Requested', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.calls.map((c) => (
                  <tr key={c.id} className="hover:bg-[#F5F4FB]/70 transition-colors align-top">
                    <td className="px-4 py-3.5">
                      {c.user ? (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#25D366]/15 text-[#10B981] flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
                            {(c.user.name || '?')[0]?.toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-[#1A1A2E] truncate">{c.user.name}</p>
                            <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]">
                              <span className="flex items-center gap-1"><Mail className="h-2.5 w-2.5" />{c.user.email}</span>
                            </div>
                            {c.user.phone && (
                              <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1">
                                <Phone className="h-2.5 w-2.5" /> {c.user.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[#9CA3AF]">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 bg-[#6C63FF]/8 text-[#6C63FF] px-2.5 py-1 rounded-lg text-xs font-medium">
                        <Clock className="h-3 w-3" />
                        {c.slot_label}
                      </div>
                      {c.slot_at && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1">
                          {new Date(c.slot_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-[#6B7280]">
                      <p className="font-medium text-[#1A1A2E]">{c.service_id || '–'}</p>
                      {c.stage && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Stage: {c.stage}</p>}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-[#6B7280] font-mono">
                      {c.order_id ? `#${c.order_id}` : '–'}
                    </td>
                    <td className="px-4 py-3.5 text-xs">
                      {c.ca?.name ? (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3 w-3 text-[#6C63FF]" />
                          <span className="text-[#1A1A2E]">{c.ca.name}</span>
                        </div>
                      ) : (
                        <span className="text-[#9CA3AF] italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {(() => {
                        // A booking auto-superseded on reschedule is cancelled,
                        // but it wasn't rejected by the CA — label it clearly.
                        const isRescheduled =
                          c.status === 'cancelled' &&
                          /^Rescheduled/i.test(c.response_notes || '');
                        return (
                          <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium capitalize ${isRescheduled ? 'bg-gray-100 text-gray-600' : (STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-600')}`}>
                            {isRescheduled ? 'Rescheduled' : c.status}
                          </span>
                        );
                      })()}
                      {c.response_notes && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1 italic max-w-[200px]" title={c.response_notes}>
                          "{c.response_notes.length > 40 ? c.response_notes.slice(0, 40) + '…' : c.response_notes}"
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-[#9CA3AF] whitespace-nowrap">
                      {new Date(c.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {c.status === 'pending' && (
                          <>
                            <button
                              onClick={() => accept(c)}
                              disabled={actingId === c.id}
                              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 transition-colors"
                            >
                              <Check className="h-3.5 w-3.5" /> Accept
                            </button>
                            <button
                              onClick={() => reject(c)}
                              disabled={actingId === c.id}
                              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
                            >
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                          </>
                        )}
                        {c.status === 'confirmed' && (
                          <button
                            onClick={() => complete(c)}
                            disabled={actingId === c.id}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/20 disabled:opacity-50 transition-colors"
                          >
                            <Check className="h-3.5 w-3.5" /> Mark complete
                          </button>
                        )}
                        {(c.status === 'completed' || c.status === 'cancelled') && (
                          <span className="text-xs text-[#9CA3AF]">–</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
